import dagre from "dagre";
import { Edge, Node } from "@xyflow/react";
import type { NodeDefinition } from "@/generated";
import { classifyEdges, GROUP_PADDING } from "@/utils/group-utils";
import {
  isMergePathsNode,
  mergeInputHandlesFromConfig,
} from "@/components/SpotflowV2/Sidebar/smartRouteBindings";

// V2 nodes carry their rendered handle order on the node data (named branches /
// paths), falling back to the registry's output_paths/input_paths — exactly
// what BaseNodeV2 renders. The single-handle defaults ("output"/"input") plus a
// trailing "on_error" mirror OutputPathsSection so the layout weights match what
// the user sees.
type V2NodeData = {
  nodeType?: string;
  outputHandles?: string[];
  inputHandles?: string[];
  nodeConfig?: Record<string, unknown>;
};

function v2HandleOrder(
  node: Node,
  direction: "outputs" | "inputs",
  registryByType: Map<string, NodeDefinition>
): string[] {
  const data = node.data as V2NodeData;
  // Merge Paths derives its input handles from config rows — the node data /
  // registry lists only hold the legacy fixed fallback.
  if (direction === "inputs" && isMergePathsNode(data.nodeType)) {
    return mergeInputHandlesFromConfig(data.nodeConfig).map((h) => h.id);
  }
  const own = direction === "outputs" ? data.outputHandles : data.inputHandles;
  let handles = Array.isArray(own) && own.length > 0 ? own : [];
  if (handles.length === 0 && data.nodeType) {
    const reg = registryByType.get(data.nodeType) as
      | { output_paths?: string[]; input_paths?: string[] }
      | undefined;
    const paths =
      direction === "outputs" ? reg?.output_paths : reg?.input_paths;
    if (paths && paths.length > 0) handles = paths;
  }
  if (direction === "outputs") {
    const base = handles.length > 0 ? handles : ["output"];
    return data.nodeConfig?.on_error_action === "skip"
      ? [...base, "on_error"]
      : base;
  }
  return handles.length > 0 ? handles : ["input"];
}

function isV2Node(node: Node | undefined): boolean {
  return node?.type === "baseNodeV2";
}

const DEFAULT_NODE_WIDTH = 400;
const DEFAULT_NODE_HEIGHT = 200;
const GROUP_LABEL_SPACE = 20;

function nodeDimensions(node: Node): { width: number; height: number } {
  const w = node.measured?.width || node.width;
  const h = node.measured?.height || node.height;
  return {
    width: typeof w === "number" ? w : DEFAULT_NODE_WIDTH,
    height: typeof h === "number" ? h : DEFAULT_NODE_HEIGHT,
  };
}

type IoDef = { name: string; keys?: string[]; visible?: boolean };

function getIoDefs(
  node: Node,
  direction: "inputs" | "outputs"
): IoDef[] | undefined {
  const config = (node.data as Record<string, unknown>)?.config as
    | Record<string, unknown>
    | undefined;
  return config?.[direction] as IoDef[] | undefined;
}

/**
 * Returns the vertical index of a handle on a node,
 * matching how Handles.tsx renders them (visible handles, keys expanded).
 */
function getHandleIndex(
  node: Node,
  handleName: string | null | undefined,
  direction: "inputs" | "outputs"
): number {
  if (!handleName) return 0;
  const ioDefs = getIoDefs(node, direction);
  if (!ioDefs) return 0;

  let index = 0;
  for (const io of ioDefs) {
    if (io.visible === false) continue;
    if (io.keys && io.keys.length > 0) {
      for (const key of io.keys) {
        if (`${io.name}::${key}` === handleName) return index;
        index++;
      }
    } else {
      if (io.name === handleName) return index;
      index++;
    }
  }
  return 0;
}

function getHandleCount(node: Node, direction: "inputs" | "outputs"): number {
  const ioDefs = getIoDefs(node, direction);
  if (!ioDefs) return 1;

  let count = 0;
  for (const io of ioDefs) {
    if (io.visible === false) continue;
    count += io.keys && io.keys.length > 0 ? io.keys.length : 1;
  }
  return Math.max(count, 1);
}

// Vertical (idx) position of an edge's handle and the total handle count on
// that side — V2 nodes use their rendered handle order, V1 nodes the legacy
// connector config.
function handlePosition(
  node: Node | undefined,
  handleName: string | null | undefined,
  direction: "outputs" | "inputs",
  registryByType: Map<string, NodeDefinition>
): { idx: number; count: number } {
  if (!node) return { idx: 0, count: 1 };
  if (isV2Node(node)) {
    const order = v2HandleOrder(node, direction, registryByType);
    const fallback = direction === "outputs" ? "output" : "input";
    const i = order.indexOf(handleName ?? fallback);
    return { idx: i >= 0 ? i : 0, count: Math.max(order.length, 1) };
  }
  return {
    idx: getHandleIndex(node, handleName, direction),
    count: getHandleCount(node, direction),
  };
}

// dagre is blind to handle Y-offsets — it treats each node as a single point,
// so a fan-out from a multi-handle node (On success / On error, Switch
// branches, …) renders with crossings dagre counts as zero. Re-order each rank
// so a node sits next to the *handle* its incoming edge leaves from: walk ranks
// left→right, key every node by the average vertical position of its source
// handles (using already-finalised predecessor positions), then redistribute
// the rank's existing y-slots in that order — preserving dagre's spacing, only
// swapping who sits where.
function orderRanksByHandleY(
  dagreNodes: Node[],
  graph: dagre.graphlib.Graph,
  edges: Edge[],
  nodeById: Map<string, Node>,
  childToGroup: Map<string, string>,
  sizeOf: (id: string) => { width: number; height: number },
  registryByType: Map<string, NodeDefinition>
): Map<string, number> {
  const centerY = new Map<string, number>();
  const ranks = new Map<number, string[]>();
  for (const n of dagreNodes) {
    const p = graph.node(n.id);
    if (!p) continue;
    centerY.set(n.id, p.y);
    const x = Math.round(p.x);
    const list = ranks.get(x) ?? [];
    list.push(n.id);
    ranks.set(x, list);
  }

  for (const x of [...ranks.keys()].sort((a, b) => a - b)) {
    const ids = ranks.get(x)!;
    if (ids.length <= 1) continue;

    const keyOf = new Map<string, number>();
    for (const id of ids) {
      const handleYs: number[] = [];
      for (const e of edges) {
        const effTarget = childToGroup.get(e.target) ?? e.target;
        if (effTarget !== id) continue;
        const effSource = childToGroup.get(e.source) ?? e.source;
        if (effSource === id) continue;
        const sp = graph.node(effSource);
        if (!sp || Math.round(sp.x) >= x) continue; // only earlier ranks
        const { idx, count } = handlePosition(
          nodeById.get(e.source),
          e.sourceHandle,
          "outputs",
          registryByType
        );
        const frac = (idx + 0.5) / count - 0.5;
        handleYs.push(
          (centerY.get(effSource) ?? sp.y) + frac * sizeOf(effSource).height
        );
      }
      keyOf.set(
        id,
        handleYs.length > 0
          ? handleYs.reduce((a, b) => a + b, 0) / handleYs.length
          : (centerY.get(id) ?? graph.node(id)!.y)
      );
    }

    const slots = ids.map((id) => graph.node(id)!.y).sort((a, b) => a - b);
    [...ids]
      .sort((a, b) => keyOf.get(a)! - keyOf.get(b)!)
      .forEach((id, i) => centerY.set(id, slots[i]));
  }

  return centerY;
}

export async function applyDagreLayout(
  nodes: Node[],
  edges: Edge[],
  nodeRegistry?: NodeDefinition[]
): Promise<Node[]> {
  const registryByType = new Map<string, NodeDefinition>(
    (nodeRegistry ?? []).map((d) => [d.type, d])
  );
  const stickyNotes: Node[] = [];
  const groupNodes: Node[] = [];
  const topLevelFlowNodes: Node[] = [];
  const childToGroup = new Map<string, string>();
  const groupChildren = new Map<string, Node[]>();
  const nodeById = new Map<string, Node>();

  for (const node of nodes) {
    nodeById.set(node.id, node);
    if (node.type === "note") {
      stickyNotes.push(node);
    } else if (node.type === "groupNode" || node.type === "groupNodeV2") {
      groupNodes.push(node);
    } else if (node.parentId) {
      childToGroup.set(node.id, node.parentId);
      const list = groupChildren.get(node.parentId) ?? [];
      list.push(node);
      groupChildren.set(node.parentId, list);
    } else {
      topLevelFlowNodes.push(node);
    }
  }

  // Pre-compute dimensions for all children (used in sub-layout, bounding box, and positioning)
  const dims = new Map<string, { width: number; height: number }>();
  for (const [, children] of groupChildren) {
    for (const child of children) {
      dims.set(child.id, nodeDimensions(child));
    }
  }

  const layoutedChildNodes: Node[] = [];
  const groupSizes = new Map<string, { width: number; height: number }>();

  for (const group of groupNodes) {
    const children = groupChildren.get(group.id) ?? [];
    if (children.length === 0) {
      groupSizes.set(group.id, nodeDimensions(group));
      continue;
    }

    const childIds = new Set(children.map((c) => c.id));
    const { internal: internalEdges } = classifyEdges(edges, childIds);

    const subGraph = new dagre.graphlib.Graph();
    subGraph.setGraph({
      rankdir: "LR",
      nodesep: 80,
      ranksep: 120,
      edgesep: 40,
      ranker: "network-simplex",
    });
    subGraph.setDefaultEdgeLabel(() => ({}));

    for (const child of children) {
      const d = dims.get(child.id)!;
      subGraph.setNode(child.id, { width: d.width, height: d.height });
    }

    for (const edge of internalEdges) {
      const s = handlePosition(
        nodeById.get(edge.source),
        edge.sourceHandle,
        "outputs",
        registryByType
      );
      const t = handlePosition(
        nodeById.get(edge.target),
        edge.targetHandle,
        "inputs",
        registryByType
      );
      const sourcePos = s.count > 1 ? s.idx / (s.count - 1) : 0.5;
      const targetPos = t.count > 1 ? t.idx / (t.count - 1) : 0.5;
      const weight = Math.max(
        1,
        Math.round(3 - ((sourcePos + targetPos) / 2) * 2)
      );
      subGraph.setEdge(edge.source, edge.target, { weight });
    }

    dagre.layout(subGraph);

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (const child of children) {
      const pos = subGraph.node(child.id);
      const d = dims.get(child.id)!;
      minX = Math.min(minX, pos.x - d.width / 2);
      minY = Math.min(minY, pos.y - d.height / 2);
      maxX = Math.max(maxX, pos.x + d.width / 2);
      maxY = Math.max(maxY, pos.y + d.height / 2);
    }

    for (const child of children) {
      const pos = subGraph.node(child.id);
      const d = dims.get(child.id)!;
      layoutedChildNodes.push({
        ...child,
        position: {
          x: pos.x - d.width / 2 - minX + GROUP_PADDING,
          y: pos.y - d.height / 2 - minY + GROUP_PADDING + GROUP_LABEL_SPACE,
        },
      });
    }

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    groupSizes.set(group.id, {
      width: contentWidth + GROUP_PADDING * 2,
      height: contentHeight + GROUP_PADDING * 2 + GROUP_LABEL_SPACE,
    });
  }

  const dagreNodes = [...topLevelFlowNodes, ...groupNodes];

  const graph = new dagre.graphlib.Graph();

  graph.setGraph({
    rankdir: "LR",
    nodesep: 110,
    ranksep: 160,
    edgesep: 50,
    ranker: "network-simplex",
  });

  graph.setDefaultEdgeLabel(() => ({}));

  dagreNodes.forEach((node) => {
    const size = groupSizes.get(node.id) ?? nodeDimensions(node);
    graph.setNode(node.id, { width: size.width, height: size.height });
  });

  const addedEdges = new Set<string>();

  // Add each source's out-edges top-handle-first so dagre's initial within-rank
  // ordering follows the visible handle order. Weight alone can't decide the
  // order of several targets that share one source, so the insertion order is
  // what actually keeps a routing node's branches from crossing. Sort is stable,
  // so edges from different sources keep their original relative order.
  const orderedEdges = [...edges].sort((a, b) => {
    if (a.source !== b.source) return 0;
    const ai = handlePosition(
      nodeById.get(a.source),
      a.sourceHandle,
      "outputs",
      registryByType
    ).idx;
    const bi = handlePosition(
      nodeById.get(b.source),
      b.sourceHandle,
      "outputs",
      registryByType
    ).idx;
    return ai - bi;
  });

  orderedEdges.forEach((edge) => {
    const effectiveSource = childToGroup.get(edge.source) ?? edge.source;
    const effectiveTarget = childToGroup.get(edge.target) ?? edge.target;

    // Skip internal group edges (both endpoints in the same group)
    if (effectiveSource === effectiveTarget) return;

    // Skip edges involving nodes not in the dagre graph (e.g. sticky notes)
    if (!graph.hasNode(effectiveSource) || !graph.hasNode(effectiveTarget))
      return;

    const edgeKey = `${effectiveSource}->${effectiveTarget}`;

    // For multi-edges between the same pair, dagre only keeps one.
    // Use the first occurrence — handle ordering is handled via node sizing.
    if (addedEdges.has(edgeKey)) return;
    addedEdges.add(edgeKey);

    // Use handle index to compute edge weight — edges near the top of the
    // handle list get higher weight so dagre keeps the "primary" connections
    // tighter and orders secondary connections below them.
    const s = handlePosition(
      nodeById.get(edge.source),
      edge.sourceHandle,
      "outputs",
      registryByType
    );
    const t = handlePosition(
      nodeById.get(edge.target),
      edge.targetHandle,
      "inputs",
      registryByType
    );
    const sourcePos = s.count > 1 ? s.idx / (s.count - 1) : 0.5;
    const targetPos = t.count > 1 ? t.idx / (t.count - 1) : 0.5;
    const avgPos = (sourcePos + targetPos) / 2;
    const weight = Math.max(1, Math.round(3 - avgPos * 2));

    graph.setEdge(effectiveSource, effectiveTarget, { weight });
  });

  dagre.layout(graph);

  const sizeOf = (id: string): { width: number; height: number } => {
    const g = groupSizes.get(id);
    if (g) return g;
    const n = nodeById.get(id);
    return n
      ? nodeDimensions(n)
      : { width: DEFAULT_NODE_WIDTH, height: DEFAULT_NODE_HEIGHT };
  };
  // Re-order each rank so fan-out edges follow their source handle's vertical
  // order (dagre is blind to handle offsets). `centerY` replaces dagre's `y`.
  const centerY = orderRanksByHandleY(
    dagreNodes,
    graph,
    edges,
    nodeById,
    childToGroup,
    sizeOf,
    registryByType
  );

  // Align nodes within a rank by their TOP edge instead of their center.
  // Nodes in the same rank share a center y; for each, find the tallest node's
  // height so their top edges all sit at the same line.
  const rankMaxHeight = new Map<number, number>();
  for (const node of dagreNodes) {
    const y = centerY.get(node.id);
    if (y === undefined) continue;
    const size = groupSizes.get(node.id) ?? nodeDimensions(node);
    const existing = rankMaxHeight.get(y) ?? 0;
    if (size.height > existing) rankMaxHeight.set(y, size.height);
  }

  const layoutedFlowNodes = topLevelFlowNodes.map((node) => {
    const pos = graph.node(node.id);
    const y = centerY.get(node.id) ?? pos.y;
    const { width } = nodeDimensions(node);
    const maxH = rankMaxHeight.get(y) ?? nodeDimensions(node).height;
    return {
      ...node,
      position: { x: pos.x - width / 2, y: y - maxH / 2 },
    };
  });

  const layoutedGroupNodes = groupNodes.map((group) => {
    const pos = graph.node(group.id);
    const y = centerY.get(group.id) ?? pos.y;
    const size = groupSizes.get(group.id) ?? nodeDimensions(group);
    const maxH = rankMaxHeight.get(y) ?? size.height;
    return {
      ...group,
      position: { x: pos.x - size.width / 2, y: y - maxH / 2 },
      width: size.width,
      height: size.height,
      style: {
        ...((group.style as Record<string, unknown>) ?? {}),
        width: size.width,
        height: size.height,
      },
    };
  });

  return [
    ...layoutedGroupNodes,
    ...layoutedChildNodes,
    ...layoutedFlowNodes,
    ...stickyNotes,
  ];
}
